/**
 * ========================================
 * CUSTOM ASSET - media-lead-modded.js
 * Version: 1.0
 * Created: 2026-10-25
 * Last Modified: 2026-10-25
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * <media-lead-modded> custom element wrapping the lead-image-only media
 * column rendered by sections/product-information-modded.liquid. On a
 * normal product page the stock variant picker morphs ONLY itself and
 * broadcasts StandardEvents.productSelect with the freshly fetched page
 * HTML; every media component is expected to update itself from that
 * event (see assets/media-gallery.js). The plain lead-image <div> had no
 * listener, so switching a color swatch never changed the photo (Adam,
 * Oct 24). This element subscribes exactly like the stock gallery and
 * swaps its image for the newly selected variant's featured image.
 *
 * MODIFICATIONS:
 * - v1.0 (2026-10-25): Initial creation.
 *
 * DEPENDENCIES:
 * - '@shopify/events' import-map entry (snippets/scripts.liquid).
 * - Markup contract: sections/product-information-modded.liquid v1.2+
 *   renders <media-lead-modded> around .media-lead-modded, with the
 *   image (or placeholder SVG) carrying .media-lead-modded__img.
 */

import { StandardEvents } from '@shopify/events';

class MediaLeadModded extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {CustomEvent & { promise: Promise<any> }} event */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;

        const nextImg = html.querySelector('media-lead-modded .media-lead-modded__img');
        if (!nextImg) return;

        const currentImg = this.querySelector('.media-lead-modded__img');
        if (!currentImg || currentImg.outerHTML === nextImg.outerHTML) return;

        const replacement = nextImg.cloneNode(true);
        // The page is already painted; don't re-assert eager/high priority.
        if (replacement instanceof HTMLImageElement) {
          replacement.loading = 'eager';
          replacement.fetchPriority = 'high';
        }
        currentImg.replaceWith(replacement);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-lead-modded] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('media-lead-modded')) {
  customElements.define('media-lead-modded', MediaLeadModded);
}
